/**
 * @fileoverview Syndicator Showdown — Notification Center
 * @module components/notifications/NotificationCenter
 *
 * Slide-in drawer listing a user's recent notifications (read + unread),
 * opened from NotificationBell. Rendered through a portal, same pattern as
 * ShareQrModal — body scroll lock, Escape-to-close, click-outside-to-close.
 */

import React, { useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useNotificationFeed } from '../../hooks/useNotificationFeed';
import {
  deriveStatus,
  isExpired,
  resolveImageType,
  getNotificationIcon,
  formatRelativeTime,
} from '../../utils/notificationHelpers';
import './NotificationCenter.css';

export default function NotificationCenter({ user, isOpen, onClose }) {
  const navigate         = useNavigate();
  const { items, unreadCount } = useNotificationFeed(user, { limit: 30 });
  const visibleItems     = items.filter((n) => !isExpired(n));

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleEsc = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  const handleRowClick = useCallback(async (notif) => {
    if (deriveStatus(notif) === 'unread') {
      try {
        await updateDoc(doc(db, 'notifications', user.uid, 'items', notif.id), {
          status: 'read', readAt: serverTimestamp(), read: true,
        });
      } catch (e) {
        console.error('[NotificationCenter] mark read error:', e.message);
      }
    }
    if (notif.ctaUrl) {
      onClose?.();
      navigate(notif.ctaUrl);
    }
  }, [user, navigate, onClose]);

  const handleMarkAllRead = useCallback(async () => {
    if (!user) return;
    const unread = visibleItems.filter((n) => deriveStatus(n) === 'unread');
    if (unread.length === 0) return;
    try {
      const batch = writeBatch(db);
      unread.forEach((n) => {
        batch.update(doc(db, 'notifications', user.uid, 'items', n.id), {
          status: 'read', readAt: serverTimestamp(), read: true,
        });
      });
      await batch.commit();
    } catch (e) {
      console.error('[NotificationCenter] mark all read error:', e.message);
    }
  }, [user, visibleItems]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="notif-center-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="notif-center-panel" role="dialog" aria-modal="true" aria-label="Notifications">
        <div className="notif-center-header">
          <h2 className="notif-center-title">Notifications</h2>
          <button type="button" className="notif-center-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="notif-center-toolbar">
          <span className="notif-center-count">
            {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
          </span>
          {unreadCount > 0 && (
            <button type="button" className="notif-center-mark-all" onClick={handleMarkAllRead}>
              Mark all as read
            </button>
          )}
        </div>

        <div className="notif-center-list">
          {visibleItems.length === 0 && (
            <p className="notif-center-empty">No notifications yet</p>
          )}

          {visibleItems.map((notif) => {
            const status   = deriveStatus(notif);
            const unread   = status === 'unread';
            const imageType = resolveImageType(notif);
            return (
              <button
                type="button"
                key={notif.id}
                className={`notif-center-row${unread ? ' notif-center-row--unread' : ''}`}
                onClick={() => handleRowClick(notif)}
              >
                <span className="notif-center-row__icon">{getNotificationIcon(imageType)}</span>
                <span className="notif-center-row__body">
                  <span className="notif-center-row__title">{notif.title}</span>
                  {notif.body && <span className="notif-center-row__text">{notif.body}</span>}
                  <span className="notif-center-row__time">{formatRelativeTime(notif.createdAt)}</span>
                </span>
                {unread && <span className="notif-center-row__dot" aria-hidden="true" />}
              </button>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
}
